import React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import differenceInDays from 'date-fns/differenceInDays';
import { MenuItemIconClassName } from '../../../../../../DesignSystem/ContextMenu/ContextMenuStyles';
import Heading from '../../../../../../generic-components/Heading';
import { BILL_ACTIONS_TEXTS } from '../../consts/actions';
import { ReactComponent as IconReminderOn } from '../../../../../../assets/icons/svg/shared/ic-shared-reminder-on.svg';
import { ReactComponent as IconReminderOff } from '../../../../../../assets/icons/svg/shared/ic-shared-reminder-off.svg';
import { useStore } from '../../../../../../store';
import { useSetBillReminder } from '../../../../../../hooks/mutations/useSetBillReminder';
import Spinner, { SpinnerColor } from '../../../../../../generic-components/Spinner';
import LiliNotification, { LiliNotificationType } from '../../../../../../DesignSystem/LiliNotification/LiliNotification';
import LiliToast, { LiliToastDefaultConfig } from '../../../../../../DesignSystem/LiliToast/LiliToast';
import { MIN_DAYS_FOR_REMINDER } from '../../../../BillDataUtils';

interface BillPaymentReminderProps {
  externalDisabled?: boolean;
}

export const BillPaymentReminder = ({ externalDisabled }: BillPaymentReminderProps) => {
  const { t } = useTranslation();
  const selectedBill = useStore(state => state.selectedBill);
  const setSelectedBill = useStore(state => state.setSelectedBill);
  const updateNewItemByExternalId = useStore(state => state.updateNewItemByExternalId);
  const isPaymentReminderOn = selectedBill?.paymentReminderData?.paymentReminderEnabled;
  const isReminderOptionEnabled = differenceInDays(new Date(selectedBill?.dueDate || ''), new Date()) >= MIN_DAYS_FOR_REMINDER;

  const { mutate, isLoading } = useSetBillReminder(
    (billItem) => {
      setSelectedBill(selectedBill?.billId || '', billItem);
      updateNewItemByExternalId(billItem);
      toast(<LiliToast text={`Payment reminder ${billItem.paymentReminderData?.paymentReminderEnabled ? 'enabled' : 'disabled'}`} id='paymentReminderToggleSuccess' />, { ...LiliToastDefaultConfig });
    },
    (errData) => {
      toast(<LiliNotification type={LiliNotificationType.ERROR} text={errData.response.data.errors[0].localizedMessage} id='paymentReminderToggleFailure' />);
    },
  );

  const getClassName = () => {
    if (externalDisabled || !isReminderOptionEnabled) {
      return 'cursor-default opacity-50 hover:bg-bw01';
    }
    return '';
  };

  return isLoading
    ? (
      <div className='flex items-center px-[16px] py-[6px] cursor-pointer hover:bg-bw00'>
        <Spinner height='2rem' spinnerColor={SpinnerColor.Blue} width='2rem' />
      </div>
    ) : (
      <button
        type='button'
        disabled={externalDisabled || !isReminderOptionEnabled}
        onClick={() => {
          if (isReminderOptionEnabled) {
            mutate({ billId: selectedBill?.billId || '', paymentReminderEnabled: !isPaymentReminderOn });
          }
        }}
        className={`flex items-center gap-[12px] px-[16px] py-[6px] cursor-pointer hover:bg-bw00 ${getClassName()}`}
      >
        {isPaymentReminderOn ? <IconReminderOn className={MenuItemIconClassName} />
          : <IconReminderOff className={MenuItemIconClassName} />}
        <div className='flex flex-col text-left'>
          <Heading
            title={`${isPaymentReminderOn ? t(BILL_ACTIONS_TEXTS.REMINDER_OFF) : t(BILL_ACTIONS_TEXTS.REMINDER_ON)}`}
            className='font-metricR text-bw08 text-14px'
          />
          <Heading
            title={`${t(BILL_ACTIONS_TEXTS.REMINDER_DESCRIPTION)}`}
            className='font-metricR text-xxs text-bw07 text-14px'
          />
        </div>
      </button>
    );
};
